import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import * as React from 'react';

import fonts from 'styles/typography.module.css';
import { DateRangeSchema } from 'common/hooks/customSearchParams';
import { RadioGroup } from 'components/tailwind-ui/RadioGroup';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { useMutation } from '@tanstack/react-query';
import {
  OutstandingFeesBreakdown,
  OutstandingFeesBreakdownParams,
  OutstandingFeesType,
  exportOutstandingFeesBreakdown,
} from 'services/clients/insights';
import { LoadingButton } from '@mui/lab';
import { CalendarPeriodFilter, CalendarPeriodFilterFunction } from './CalendarPeriodFilter';
import { Tabs, Value } from './Tabs';
import { HistoricalPerDiemCalendarPeriod } from '../params';
import { convertDateRangeSchemaToStartAndEnd } from './hooks';

export const ExportContext = React.createContext<{
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}>({ open: false, setOpen: () => {} });

const radioOptions: { label: string; value: string; breakdown?: OutstandingFeesBreakdown }[] = [
  { label: 'By date', value: 'date' },
  { label: 'By date + ports', value: 'date-ports', breakdown: OutstandingFeesBreakdown.Port },
  { label: 'By date + SSL', value: 'date-ssl', breakdown: OutstandingFeesBreakdown.ShippingLine },
  {
    label: 'By date + container #',
    value: 'date-container',
    breakdown: OutstandingFeesBreakdown.Container,
  },
  {
    label: 'By date + ocean BOL',
    value: 'date-ocean-bol',
    breakdown: OutstandingFeesBreakdown.OceanBOL,
  },
  /*
  { label: 'By date + customers', value: 'date-customers' },
  { label: 'By date + drayage carriers', value: 'date-drayage-carriers' },
  { label: 'By date + business unit', value: 'date-business-unit' },
  { label: 'By date + assignee', value: 'date-assignee' },
  { label: 'By date + PO #', value: 'date-po' },
  { label: 'By date + House BOL', value: 'date-house-bol' },
  { label: 'By date + warehouse lot #', value: 'date-warehouse-lot' },
  */
];

const calendarPeriodFilterFunction: CalendarPeriodFilterFunction = (parsedDates, period) => {
  const dayDiff = parsedDates.endDate?.diff(parsedDates.startDate, 'days') || 0;
  const weekDiff = parsedDates.endDate?.diff(parsedDates.startDate, 'weeks') || 0;
  const monthDiff = parsedDates.endDate?.diff(parsedDates.startDate, 'months') || 0;

  switch (period) {
    case HistoricalPerDiemCalendarPeriod.Day:
      return true;
    case HistoricalPerDiemCalendarPeriod.Week:
      return weekDiff >= 1;
    case HistoricalPerDiemCalendarPeriod.Month:
      return dayDiff >= 30;
    case HistoricalPerDiemCalendarPeriod.Quarter:
      return monthDiff >= 3;
    default:
      return false;
  }
};

interface ExportModalState {
  calendarPeriod: HistoricalPerDiemCalendarPeriod;
  dateRange: DateRangeSchema;
  exportType: string;
  custom?: boolean;
}

export const ExportModal: React.FC = () => {
  const downloadRef = React.useRef<HTMLIFrameElement>(null);
  const [open, setOpen] = React.useState(false);

  const [exportState, setExportRequest] = React.useState<ExportModalState>({
    calendarPeriod: HistoricalPerDiemCalendarPeriod.Day,
    dateRange: {
      startDate: '-30:days',
      endDate: 'today',
      exactDate: null,
      includeEndDate: false,
      includeStartDate: false,
    },
    exportType: 'date',
    custom: false,
  });

  const handleChange = (v: Value) => {
    setExportRequest((prev) => ({ ...prev, ...v }));
  };

  const exportMutation = useMutation(exportOutstandingFeesBreakdown);

  const sendExportRequest = () => {
    const { startDate, endDate } = convertDateRangeSchemaToStartAndEnd(exportState.dateRange) || {};
    const breakdown = radioOptions.find((o) => o.value === exportState.exportType)?.breakdown;

    const request: OutstandingFeesBreakdownParams = {
      type: OutstandingFeesType.PerDiem,
      breakdown,
      startDate,
      endDate,
      calendarPeriod: exportState.calendarPeriod,
    };
    exportMutation.mutate(request, {
      onSuccess: (data) => {
        if (downloadRef.current) {
          downloadRef.current.src = data.exportLink;
        }
        setOpen(false);
      },
    });
  };

  return (
    <div className="flex">
      <iframe ref={downloadRef} id="my_iframe" style={{ display: 'none' }} title="download" />
      <Button
        className="rounded-sm shadow-sm"
        variant="outlined"
        color="info"
        onClick={() => setOpen(true)}
        style={{ paddingTop: '0.125rem', paddingBottom: '0.125rem' }}
      >
        Export data
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        className={fonts.font}
      >
        <DialogTitle className="flex justify-between py-2 items-center border-b border-gray-200">
          Export data
          <IconButton className="rounded-full" color="info" onClick={() => setOpen(false)}>
            <XMarkIcon className="h-5 w-5 text-gray-900" />
          </IconButton>
        </DialogTitle>
        <DialogContent className="py-8">
          <div className="flex flex-col space-y-12">
            <div className="flex-col space-y-5">
              <h3>Select date range and breakdown</h3>
              <div className="flex gap-4">
                <Tabs
                  value={exportState}
                  onChange={(v) => handleChange({ ...v, calendarPeriod: null })}
                />
                <CalendarPeriodFilter
                  onChange={(period) => {
                    setExportRequest((prev) => ({ ...prev, calendarPeriod: period }));
                  }}
                  calendarPeriod={exportState.calendarPeriod}
                  dateRange={exportState.dateRange}
                  filterFunction={calendarPeriodFilterFunction}
                />
              </div>
            </div>
            <div className="flex-col space-y-5">
              <h3>How would you like to export your spreadsheet?</h3>
              <RadioGroup
                options={radioOptions}
                filterState={exportState.exportType}
                onChange={(value: string) => {
                  setExportRequest((prev) => ({ ...prev, exportType: value }));
                }}
                className="flex-wrap gap-4"
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions className="border-t border-gray-200">
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={sendExportRequest}
            className="float-right mx-5 my-1"
            loading={exportMutation.isLoading}
          >
            Export data
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};
ExportModal.displayName = 'HistoricalPerDiem.FilterBar.ExportModal';
